(() => {
  const AUTOPLAY_INTERVAL = 5000;
  const HUD_IDLE_DELAY = 3500;

  function getFullscreenElement() {
    return document.fullscreenElement || document.webkitFullscreenElement || null;
  }

  function isNodeFullscreen(node) {
    const fullscreenElement = getFullscreenElement();

    return Boolean(fullscreenElement && (fullscreenElement === node || node.contains(fullscreenElement)));
  }

  function requestNodeFullscreen(node) {
    if (node.requestFullscreen) {
      return node.requestFullscreen();
    }

    if (node.webkitRequestFullscreen) {
      return node.webkitRequestFullscreen();
    }

    return Promise.resolve();
  }

  function exitBrowserFullscreen() {
    if (document.exitFullscreen) {
      return document.exitFullscreen();
    }

    if (document.webkitExitFullscreen) {
      return document.webkitExitFullscreen();
    }

    return Promise.resolve();
  }

  function formatDuration(milliseconds) {
    const totalSeconds = Math.max(0, Math.floor(milliseconds / 1000));
    const minutes = String(Math.floor(totalSeconds / 60)).padStart(2, "0");
    const seconds = String(totalSeconds % 60).padStart(2, "0");

    return `${minutes}:${seconds}`;
  }

  function isLightColor(hex) {
    if (!hex || typeof hex !== "string") {
      return false;
    }

    const normalized = hex.replace("#", "");

    if (!/^[0-9a-fA-F]{6}$/.test(normalized)) {
      return false;
    }

    const red = parseInt(normalized.slice(0, 2), 16) / 255;
    const green = parseInt(normalized.slice(2, 4), 16) / 255;
    const blue = parseInt(normalized.slice(4, 6), 16) / 255;
    const luminance = 0.2126 * red + 0.7152 * green + 0.0722 * blue;

    return luminance > 0.9;
  }

  function parseColors(node) {
    try {
      const colors = JSON.parse(node.dataset.pixelColors || "[]");

      return Array.isArray(colors) && colors.length ? colors : [];
    } catch (error) {
      return [];
    }
  }

  function lockPage() {
    document.documentElement.classList.add("pixel-checker-page-active");
    document.body.classList.add("pixel-checker-page-active");
  }

  function unlockPage() {
    document.documentElement.classList.remove("pixel-checker-page-active");
    document.body.classList.remove("pixel-checker-page-active");
  }

  function initPixelChecker(node) {
    if (!node || node.dataset.pixelCheckerInitialized === "true") {
      return;
    }

    const colors = parseColors(node);

    if (!colors.length) {
      return;
    }

    node.dataset.pixelCheckerInitialized = "true";

    const screen = node.querySelector("[data-pixel-screen]");
    const intro = node.querySelector("[data-pixel-intro]");
    const testUi = node.querySelector("[data-pixel-test-ui]");
    const watermark = node.querySelector("[data-pixel-watermark]");
    const currentLabel = node.querySelector("[data-pixel-current]");
    const progressLabel = node.querySelector("[data-pixel-progress]");
    const elapsedLabel = node.querySelector("[data-pixel-elapsed]");
    const modeLabel = node.querySelector("[data-pixel-mode]");
    const announcer = node.querySelector("[data-pixel-announcer]");
    const cta = node.querySelector("[data-pixel-cta]");
    const autoButton = node.querySelector('[data-pixel-action="autoplay"]');
    const fullscreenButton = node.querySelector('[data-pixel-action="fullscreen"]');
    const swatches = Array.from(node.querySelectorAll("[data-pixel-swatch]"));

    let currentIndex = 0;
    let hasStarted = false;
    let autoplayTimer = null;
    let hudTicker = null;
    let hudHideTimer = null;
    let nextAutoplayAt = null;
    let sessionStartedAt = Date.now();
    let frozenElapsedMs = null;
    let isHudHidden = false;

    function syncWatermarkFromHeader() {
      if (!watermark || watermark.dataset.pixelWatermarkFromHeader !== "true") {
        return;
      }

      const headerLogo = document.querySelector(".header__logo-image:not(.header__logo-image--transparent)");

      if (!headerLogo) {
        return;
      }

      let watermarkImage = watermark.querySelector(".pixel-checker__watermark-image");

      if (!watermarkImage) {
        watermark.querySelector(".pixel-checker__watermark-text")?.remove();

        watermarkImage = document.createElement("img");
        watermarkImage.className = "pixel-checker__watermark-image";
        watermarkImage.loading = "lazy";
        watermark.appendChild(watermarkImage);
      }

      const src = headerLogo.currentSrc || headerLogo.getAttribute("src");
      const srcset = headerLogo.getAttribute("srcset");
      const sizes = headerLogo.getAttribute("sizes");
      const width = headerLogo.getAttribute("width");
      const height = headerLogo.getAttribute("height");
      const alt = headerLogo.getAttribute("alt");

      if (src) {
        watermarkImage.src = src;
      }

      if (srcset) {
        watermarkImage.setAttribute("srcset", srcset);
      } else {
        watermarkImage.removeAttribute("srcset");
      }

      if (sizes) {
        watermarkImage.setAttribute("sizes", sizes);
      }

      if (width) {
        watermarkImage.setAttribute("width", width);
      }

      if (height) {
        watermarkImage.setAttribute("height", height);
      }

      watermarkImage.alt = alt || watermarkImage.alt || "Logo";
    }

    function syncStage() {
      node.classList.toggle("is-testing", hasStarted);
      node.classList.toggle("is-hud-hidden", hasStarted && isHudHidden);

      if (hasStarted) {
        lockPage();
      } else {
        unlockPage();
      }

      if (intro) {
        intro.setAttribute("aria-hidden", String(hasStarted));
      }

      if (testUi) {
        testUi.setAttribute("aria-hidden", String(!hasStarted));
      }
    }

    function clearHudHideTimer() {
      if (hudHideTimer) {
        clearTimeout(hudHideTimer);
        hudHideTimer = null;
      }
    }

    function setHudHidden(hidden) {
      isHudHidden = hidden;
      node.classList.toggle("is-hud-hidden", hasStarted && isHudHidden);
    }

    function scheduleHudHide() {
      clearHudHideTimer();

      if (!hasStarted || (cta && !cta.hidden)) {
        setHudHidden(false);
        return;
      }

      hudHideTimer = window.setTimeout(() => {
        setHudHidden(true);
      }, HUD_IDLE_DELAY);
    }

    function pingHud() {
      if (!hasStarted || (cta && !cta.hidden)) {
        return;
      }

      setHudHidden(false);
      scheduleHudHide();
    }

    function syncFullscreenButton() {
      if (!fullscreenButton) {
        return;
      }

      const active = isNodeFullscreen(node);
      fullscreenButton.classList.toggle("is-active", active);
      fullscreenButton.setAttribute("aria-pressed", String(active));
    }

    function updateHud() {
      const elapsedMs = hasStarted ? (frozenElapsedMs === null ? Date.now() - sessionStartedAt : frozenElapsedMs) : 0;

      if (elapsedLabel) {
        elapsedLabel.textContent = formatDuration(elapsedMs);
      }

      if (modeLabel) {
        if (!hasStarted) {
          modeLabel.textContent = "Ready to start";
        } else if (autoplayTimer && nextAutoplayAt) {
          const secondsLeft = Math.max(1, Math.ceil((nextAutoplayAt - Date.now()) / 1000));
          modeLabel.textContent = `Auto mode / ${secondsLeft}s`;
        } else {
          modeLabel.textContent = "Manual mode";
        }
      }

      if (autoButton) {
        const active = Boolean(autoplayTimer);
        autoButton.classList.toggle("is-active", active);
        autoButton.setAttribute("aria-pressed", String(active));
      }

      syncFullscreenButton();
    }

    function renderColor() {
      const currentColor = colors[currentIndex];

      if (screen) {
        screen.style.backgroundColor = currentColor.hex;
      }

      node.classList.toggle("is-light-screen", isLightColor(currentColor.hex));

      if (currentLabel) {
        currentLabel.textContent = currentColor.name;
      }

      if (progressLabel) {
        progressLabel.textContent = `${currentIndex + 1} / ${colors.length}`;
      }

      swatches.forEach((swatch, index) => {
        const active = index === currentIndex;
        swatch.classList.toggle("is-active", active);
        swatch.setAttribute("aria-pressed", String(active));
      });

      if (announcer) {
        announcer.textContent = `${currentColor.name} test screen active.`;
      }
    }

    function stopAutoplay() {
      if (autoplayTimer) {
        clearInterval(autoplayTimer);
        autoplayTimer = null;
      }

      nextAutoplayAt = null;
      updateHud();
    }

    function showCompletion() {
      if (!hasStarted) {
        return;
      }

      clearHudHideTimer();
      setHudHidden(false);
      stopAutoplay();
      frozenElapsedMs = Date.now() - sessionStartedAt;

      if (cta) {
        cta.hidden = false;
      }

      if (isNodeFullscreen(node)) {
        Promise.resolve(exitBrowserFullscreen()).catch(() => {});
      }

      updateHud();
    }

    function hideCompletion() {
      if (cta) {
        cta.hidden = true;
      }

      frozenElapsedMs = null;
      pingHud();
      updateHud();
    }

    function goToColor(index) {
      if (!hasStarted) {
        return;
      }

      if (cta && !cta.hidden) {
        hideCompletion();
      }

      currentIndex = index;
      renderColor();

      if (autoplayTimer) {
        nextAutoplayAt = Date.now() + AUTOPLAY_INTERVAL;
      }

      updateHud();
    }

    function move(direction) {
      if (!hasStarted) {
        return;
      }

      if (direction > 0 && currentIndex === colors.length - 1) {
        showCompletion();
        return;
      }

      const nextIndex = (currentIndex + direction + colors.length) % colors.length;
      goToColor(nextIndex);
    }

    function startAutoplay() {
      if (!hasStarted) {
        return;
      }

      if (cta && !cta.hidden) {
        hideCompletion();
      }

      stopAutoplay();
      nextAutoplayAt = Date.now() + AUTOPLAY_INTERVAL;

      autoplayTimer = window.setInterval(() => {
        move(1);
      }, AUTOPLAY_INTERVAL);

      updateHud();
    }

    function startTest(requestFullscreenNow = false) {
      hasStarted = true;
      stopAutoplay();
      sessionStartedAt = Date.now();
      frozenElapsedMs = null;
      currentIndex = 0;

      if (cta) {
        cta.hidden = true;
      }

      syncStage();
      renderColor();
      setHudHidden(false);
      updateHud();
      scheduleHudHide();

      if (requestFullscreenNow) {
        Promise.resolve(requestNodeFullscreen(node)).catch(() => {});
      }
    }

    function returnToIntro() {
      clearHudHideTimer();
      stopAutoplay();
      hasStarted = false;
      sessionStartedAt = Date.now();
      frozenElapsedMs = null;
      currentIndex = 0;
      setHudHidden(false);

      if (cta) {
        cta.hidden = true;
      }

      syncStage();
      renderColor();
      updateHud();
    }

    function restartTest() {
      startTest(true);
    }

    function handleAction(action) {
      if (!action) {
        return;
      }

      if (action === "start") {
        startTest(true);
        return;
      }

      if (action === "prev") {
        move(-1);
        return;
      }

      if (action === "next") {
        move(1);
        return;
      }

      if (action === "autoplay") {
        if (autoplayTimer) {
          stopAutoplay();
        } else {
          startAutoplay();
        }

        return;
      }

      if (action === "fullscreen") {
        if (isNodeFullscreen(node)) {
          exitBrowserFullscreen();
        } else {
          requestNodeFullscreen(node);
        }

        return;
      }

      if (action === "exit") {
        if (isNodeFullscreen(node)) {
          Promise.resolve(exitBrowserFullscreen()).catch(() => {});
        }

        returnToIntro();
        return;
      }

      if (action === "restart") {
        restartTest();
      }
    }

    screen?.addEventListener("click", () => {
      if (!hasStarted || (cta && !cta.hidden)) {
        return;
      }

      pingHud();
      move(1);
    });

    swatches.forEach((swatch) => {
      swatch.addEventListener("click", () => {
        if (!hasStarted || (cta && !cta.hidden)) {
          return;
        }

        const index = Number(swatch.dataset.pixelSwatch);

        if (!Number.isNaN(index)) {
          pingHud();
          goToColor(index);
        }
      });
    });

    node.addEventListener("click", (event) => {
      const actionTarget = event.target.closest("[data-pixel-action]");

      if (!actionTarget) {
        return;
      }

      event.preventDefault();
      pingHud();
      handleAction(actionTarget.dataset.pixelAction);
    });

    ["pointermove", "pointerdown", "wheel", "keydown"].forEach((eventName) => {
      node.addEventListener(
        eventName,
        () => {
          pingHud();
        },
        { passive: true }
      );
    });

    hudTicker = window.setInterval(updateHud, 250);

    syncWatermarkFromHeader();
    syncStage();
    renderColor();
    updateHud();

    document.addEventListener("fullscreenchange", syncFullscreenButton);
    document.addEventListener("webkitfullscreenchange", syncFullscreenButton);

    window.addEventListener(
      "pagehide",
      () => {
        if (hudTicker) {
          clearInterval(hudTicker);
        }

        clearHudHideTimer();
        stopAutoplay();
        unlockPage();
      },
      { once: true }
    );
  }

  function initAll(root = document) {
    root.querySelectorAll("[data-pixel-checker]").forEach(initPixelChecker);
  }

  initAll(document);
  document.addEventListener("shopify:section:load", (event) => initAll(event.target));
})();
